import { useRef, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import { Box, Button, LinearProgress, Link, Typography, Alert } from '@mui/material';
import { CloudUpload, PictureAsPdf, CheckCircle } from '@mui/icons-material';
import { msg } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import UPLOAD_DEVICE_DOCUMENT from '../../graphql/mutations/uploadDeviceDocument';
import DEVICE_DOCUMENT_PRESIGNED_URL from '../../graphql/querys/deviceDocumentPresignedUrl';
import { useModalStatusMessage } from '@/hooks';

import type { DeviceDocumentType } from '@/__generated__/graphql';

interface PDFUploadFieldProps {
  label: string;
  currentUrl?: string | null;
  documentType: DeviceDocumentType;
  deviceId: string;
  onFileKeyReady: (fileKey: string, finalUrl: string) => void;
  onClearFile: () => void;
  disabled?: boolean;
}

export const PDFUploadField = ({
  label,
  currentUrl,
  documentType,
  deviceId,
  onFileKeyReady,
  onClearFile,
  disabled = false
}: PDFUploadFieldProps) => {
  const { _ } = useLingui();
  const { openModalErrorMessage } = useModalStatusMessage();
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploadedFileKey, setUploadedFileKey] = useState<string | null>(null);
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);

  const [initiateUpload] = useMutation(UPLOAD_DEVICE_DOCUMENT);
  const [fetchPresignedUrl] = useLazyQuery(DEVICE_DOCUMENT_PRESIGNED_URL);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    // Permitir volver a cargar el mismo archivo después de quitar
    event.target.value = '';

    // Validar tipo
    if (file.type !== 'application/pdf') {
      openModalErrorMessage(_(msg`El archivo debe ser un PDF`));
      return;
    }

    // Validar tamaño (10MB)
    if (file.size > 10 * 1024 * 1024) {
      openModalErrorMessage(_(msg`El archivo no debe superar 10MB`));
      return;
    }

    try {
      setUploading(true);
      setProgress(0);

      // Fase 1: Obtener presigned URL
      const { data } = await initiateUpload({
        variables: {
          deviceId,
          documentType,
          contentType: 'application/pdf'
        }
      });

      if (!data?.uploadDeviceDocument) {
        throw new Error('No upload session received');
      }

      const { uploadUrl, fileKey, finalUrl } = data.uploadDeviceDocument;

      // Fase 2: Upload directo a S3 con progress tracking
      await uploadToS3(uploadUrl, file, setProgress);

      // Guardar fileKey para confirmación posterior
      setUploadedFileKey(fileKey);
      setUploadedFileName(file.name);
      
      // Notificar al componente padre con el fileKey
      onFileKeyReady(fileKey, finalUrl);

    } catch (error: any) {
      openModalErrorMessage(_(msg`Error al subir el archivo: ${error.message}`));
      onClearFile();
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const handleClear = () => {
    setUploadedFileKey(null);
    setUploadedFileName(null);
    onClearFile();
    // Limpiar el input file para permitir volver a cargar el mismo archivo
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const uploadToS3 = (url: string, file: File, onProgress: (p: number) => void): Promise<void> => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percentComplete = (e.loaded / e.total) * 100;
          onProgress(percentComplete);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      });

      xhr.addEventListener('error', () => reject(new Error('Upload failed')));

      xhr.open('PUT', url);
      xhr.setRequestHeader('Content-Type', 'application/pdf');
      xhr.send(file);
    });
  };

  const hasFile = currentUrl || uploadedFileKey;
  const showNewUpload = !!uploadedFileKey;

  return (
    <Box>
      <Typography variant="body2" color="textSecondary" mb={1}>
        {label}
      </Typography>

      {/* Archivo actual desde BD */}
      {currentUrl && !uploadedFileKey && (
        <Box display="flex" alignItems="center" mb={1}>
          <PictureAsPdf color="error" sx={{ mr: 1 }} />
          <Link
            component="button"
            onClick={async () => {
              const { data } = await fetchPresignedUrl({ variables: { fileUrl: currentUrl } });
              if (data?.deviceDocumentPresignedUrl) {
                window.open(data.deviceDocumentPresignedUrl, '_blank', 'noopener,noreferrer');
              }
            }}
            sx={{ mr: 2 }}
          >
            {_(msg`Ver archivo actual`)}
          </Link>
        </Box>
      )}

      {/* Archivo recién subido (pendiente de confirmación) */}
      {showNewUpload && (
        <Alert severity="success" icon={<CheckCircle />} sx={{ mb: 1 }}>
          {_(msg`Archivo cargado`)}: {uploadedFileName}
          <Typography variant="caption" display="block" color="text.secondary">
            {_(msg`Se confirmará al guardar la configuración`)}
          </Typography>
        </Alert>
      )}

      {/* Botón de selección/cambio */}
      <Box display="flex" gap={1}>
        <Button
          variant="outlined"
          component="label"
          startIcon={<CloudUpload />}
          disabled={disabled || uploading}
          fullWidth={!hasFile}
        >
          {uploading 
            ? _(msg`Subiendo...`) 
            : hasFile 
              ? _(msg`Cambiar PDF`) 
              : _(msg`Seleccionar PDF`)
          }
          <input
            type="file"
            hidden
            accept="application/pdf"
            onChange={handleFileSelect}
            disabled={disabled || uploading}
            ref={fileInputRef}
          />
        </Button>

        {hasFile && !disabled && (
          <Button
            variant="text"
            color="error"
            onClick={handleClear}
            disabled={uploading}
          >
            {_(msg`Quitar`)}
          </Button>
        )}
      </Box>

      {/* Progress bar */}
      {uploading && (
        <Box mt={2}>
          <LinearProgress variant="determinate" value={progress} />
          <Typography variant="caption" color="textSecondary" align="center" display="block">
            {Math.round(progress)}%
          </Typography>
        </Box>
      )}
    </Box>
  );
};
